/**
 * Dashboard Repository
 * Handles business logic and data transformation between API and domain
 */

import { DashboardApi } from './dashboard-api';
import {
  FleetSummary,
  Machine,
  MachineMapPoint,
  Connector,
  ConnectorStatus,
  MachineStatus,
  SummaryPeriod,
  AnalyticsDateRange,
  EnergyDataPoint,
  RevenueDataPoint,
  AnalyticsSummary,
  TopLocation,
  ActiveSession,
  RecentSession,
  SessionStatus,
  AlertSummary,
  Alert,
  AlertSeverity,
  AlertStatus,
  AlertType,
  AccountSummary,
  Subscription,
  PaymentToday,
} from '../types/dashboard-types';
import { MachineFilters, AlertFilters } from '../types/filter-types';
import {
  FleetStatusParams,
  AnalyticsParams,
  TopLocationsParams,
  ActiveSessionsParams,
  RecentSessionsParams,
  AlertsParams,
  SubscriptionsParams,
  ConnectorStatusType,
} from '../types/api-types';

function safeDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return isNaN(d.getTime()) ? undefined : d;
}

export class DashboardRepository {
  constructor(private readonly api: DashboardApi) {}

  // ============================================
  // Fleet Methods
  // ============================================

  async getFleetSummary(
    actorIds?: number[],
    period?: SummaryPeriod,
    customDateRange?: { from: Date; to: Date }
  ): Promise<FleetSummary> {
    // Fetch fleet summary (includes offline machines count)
    const response = await this.api.getFleetSummary({
      actorIds,
      period,
      from: customDateRange?.from.toISOString(),
      to: customDateRange?.to.toISOString(),
    });

    return {
      // Machine counts by connection status
      totalMachines: response.totalMachines,
      onlineMachines: response.onlineMachines,
      offlineMachines: response.offlineMachines,
      disconnectedMachines: response.disconnectedMachines,

      // Machine counts by OCPP status
      availableMachines: response.availableMachines,
      preparingMachines: response.preparingMachines,
      chargingMachines: response.chargingMachines,
      finishingMachines: response.finishingMachines,
      suspendedEVMachines: response.suspendedEVMachines,
      suspendedEVSEMachines: response.suspendedEVSEMachines,
      faultedMachines: response.faultedMachines,
      unavailableMachines: response.unavailableMachines,
      reservedMachines: response.reservedMachines,

      // Connector counts by OCPP status
      totalConnectors: response.totalConnectors,
      availableConnectors: response.availableConnectors,
      preparingConnectors: response.preparingConnectors,
      chargingConnectors: response.chargingConnectors,
      finishingConnectors: response.finishingConnectors,
      suspendedEVConnectors: response.suspendedEVConnectors,
      suspendedEVSEConnectors: response.suspendedEVSEConnectors,
      faultedConnectors: response.faultedConnectors,
      unavailableConnectors: response.unavailableConnectors,
      reservedConnectors: response.reservedConnectors,
      offlineConnectors: response.offlineConnectors,
      offlineConnectorsLessThan1Day: response.offlineConnectorsLessThan1Day,
      offlineConnectorsLessThan1Week: response.offlineConnectorsLessThan1Week,
      offlineConnectorsMoreThan1Week: response.offlineConnectorsMoreThan1Week,

      // Period-filtered metrics
      totalEnergySupplied: response.totalEnergySupplied,
      totalSessions: response.totalSessions,
      totalBuyEnergy: response.totalBuyEnergy,
      totalSaleEnergy: response.totalSaleEnergy,
      totalRevEnergy: response.totalRevEnergy,

      // Legacy fields for backwards compatibility
      totalEnergyToday: response.totalEnergySupplied,
      totalRevenueToday: 0, // Not available in new API
      activeSessionsCount: response.chargingMachines,

      // Metadata
      periodStart: response.periodStart ? new Date(response.periodStart) : undefined,
      periodEnd: response.periodEnd ? new Date(response.periodEnd) : undefined,
      lastUpdated: new Date(response.lastUpdated || Date.now()),
    };
  }

  async getFleetStatus(
    page: number,
    pageSize: number,
    filters?: MachineFilters,
    actorIds?: number[]
  ): Promise<{ machines: Machine[]; totalCount: number }> {
    // Map machine status filter: use first status if single status, otherwise use new API params
    const machineStatus = filters?.status?.length === 1 ? filters.status[0] : undefined;

    const params: FleetStatusParams = {
      actorIds,
      search: filters?.searchTerm,
      onlineOnly: filters?.status?.includes('Online') && filters.status.length === 1,
      machineStatus,                          // NEW: Pass machine status filter
      connectorStatus: filters?.connectorStatus,  // NEW: Pass connector status filter
      sessionStatus: filters?.sessionStatus,      // NEW: Pass session status filter
      skip: page * pageSize,
      take: pageSize,
    };

    const response = await this.api.getFleetStatus(params);
    const items = Array.isArray(response.items) ? response.items : [];

    const machines: Machine[] = items.map(m => ({
      machineId: m.machineId,
      name: m.machineName,
      manufacturerName: m.manufacturerName || 'Unknown',
      manufacturerId: m.manufacturerId,
      model: m.model,
      locationId: m.actorId,
      locationName: m.actorName,
      city: '', // Not in API response
      status: this.mapMachineStatus(m.status, m.isConnected),
      statusDetails: m.statusDetails,
      connectors: Array.isArray(m.connectors) ? m.connectors.map(c => ({
        connectorId: c.connectorId,
        type: c.isDc ? 'CCS2' : 'Type2', // Simplified mapping
        status: this.mapConnectorStatus(c.status),
        currentPower: c.meterValue ? c.meterValue / 1000 : 0, // Convert Wh to kWh if present
        maxPower: c.isDc ? 150 : 22, // Typical max power for DC/AC
      })) : [],
      lastHeartbeat: safeDate(m.lastHeartbeat || m.lastConnected || m.lastDisconnected) ?? new Date(),
      energyToday: 0, // Not in API response
      revenueToday: 0, // Not in API response
      activeSessionsCount: Array.isArray(m.connectors) ? m.connectors.filter(c => c.status === 'Charging').length : 0,
    }));

    return {
      machines,
      totalCount: response.totalCount ?? 0,
    };
  }

  async getFleetMap(): Promise<MachineMapPoint[]> {
    const response = await this.api.getFleetMap();
    return (response.points ?? []).map(p => ({
      machineId: p.machineId,
      machineName: p.machineName,
      actorName: p.actorName,
      latitude: p.latitude,
      longitude: p.longitude,
      status: p.status,
      isConnected: p.isConnected,
      connectorCount: p.connectorCount,
      lastHeartbeat: safeDate(p.lastHeartbeat),
    }));
  }

  /**
   * Map machine status from API to domain type
   * Falls back to isConnected if status not provided (backwards compatibility)
   */
  private mapMachineStatus(status: string | undefined, isConnected: boolean): MachineStatus {
    if (!status) {
      // Backwards compatibility: Use isConnected if status not provided
      return isConnected ? 'Available' : 'Unavailable';
    }

    switch (status) {
      case 'Available':
        return 'Available';
      case 'Preparing':
        return 'Preparing';
      case 'Charging':
        return 'Charging';
      case 'Finishing':
        return 'Finishing';
      case 'SuspendedEV':
        return 'SuspendedEV';
      case 'SuspendedEVSE':
        return 'SuspendedEVSE';
      case 'Faulted':
        return 'Faulted';
      case 'Unavailable':
        return 'Unavailable';
      case 'Reserved':
        return 'Reserved';
      default:
        return 'Unavailable';
    }
  }

  /**
   * Map connector status from API to domain type
   */
  private mapConnectorStatus(status: ConnectorStatusType): ConnectorStatus {
    switch (status) {
      case 'Available':
        return 'Available';
      case 'Preparing':
        return 'Preparing';
      case 'Charging':
        return 'Charging';
      case 'Finishing':
        return 'Finishing';
      case 'SuspendedEV':
        return 'SuspendedEV';
      case 'SuspendedEVSE':
        return 'SuspendedEVSE';
      case 'Faulted':
        return 'Faulted';
      case 'Unavailable':
        return 'Unavailable';
      case 'Reserved':
        return 'Reserved';
      default:
        return 'Unavailable';
    }
  }

  // ============================================
  // Analytics Methods
  // ============================================

  async getEnergyAnalytics(
    dateRange: AnalyticsDateRange,
    actorIds?: number[]
  ): Promise<{
    dataPoints: EnergyDataPoint[];
    summary: Partial<AnalyticsSummary>;
  }> {
    const params: AnalyticsParams = {
      actorIds,
      from: dateRange.dateFrom,
      to: dateRange.dateTo,
      groupBy: 'day',
    };

    const response = await this.api.getEnergyAnalytics(params);
    const items = Array.isArray(response.items) ? response.items : [];

    return {
      dataPoints: items.map(d => ({
        date: d.date,
        timestamp: new Date(d.date),
        energyKwh: d.energyKwh,
        peakPowerKw: 0, // Not in API response
        sessionsCount: d.sessionCount,
      })),
      summary: {
        totalEnergy: response.totalEnergyKwh ?? 0,
        totalSessions: items.reduce((sum, d) => sum + (d.sessionCount ?? 0), 0),
        averageEnergyPerSession: response.averageDailyKwh ?? 0,
        peakPowerKw: response.peakDailyKwh ?? 0,
        energyChangePercent: 0, // Not in API response
      },
    };
  }

  async getRevenueAnalytics(
    dateRange: AnalyticsDateRange,
    actorIds?: number[]
  ): Promise<{
    dataPoints: RevenueDataPoint[];
    summary: Partial<AnalyticsSummary>;
  }> {
    const params: AnalyticsParams = {
      actorIds,
      from: dateRange.dateFrom,
      to: dateRange.dateTo,
      groupBy: 'day',
    };

    const response = await this.api.getRevenueAnalytics(params);
    const items = Array.isArray(response.items) ? response.items : [];

    return {
      dataPoints: items.map(d => ({
        date: d.date,
        timestamp: new Date(d.date),
        revenueIls: d.revenue,
        transactionsCount: d.sessionCount,
        averageTransactionIls: d.sessionCount > 0 ? d.revenue / d.sessionCount : 0,
      })),
      summary: {
        totalRevenue: response.totalRevenue ?? 0,
        averageRevenuePerSession: response.averageSessionRevenue ?? 0,
        revenueChangePercent: 0, // Not in API response
      },
    };
  }

  async getTopLocations(
    dateRange: AnalyticsDateRange,
    limit: number = 10,
    sortBy: 'revenue' | 'energy' | 'sessions' = 'revenue'
  ): Promise<TopLocation[]> {
    const params: TopLocationsParams = {
      from: dateRange.dateFrom,
      to: dateRange.dateTo,
      limit,
      sortBy,
    };

    const response = await this.api.getTopLocations(params);
    // API returns flat array directly, not wrapped in { items: [] }
    const items = Array.isArray(response) ? response : [];

    return items.map((loc) => ({
      rank: loc.rank,
      locationId: loc.actorId,
      locationName: loc.actorName,
      city: '', // Not in API response
      energyKwh: loc.energyKwh,       // API uses energyKwh
      revenueIls: loc.revenue,        // API uses revenue
      sessionsCount: loc.sessionCount,
    }));
  }

  // ============================================
  // Sessions Methods
  // ============================================

  async getActiveSessions(actorIds?: number[]): Promise<{
    sessions: ActiveSession[];
    totalActiveEnergy: number;
    totalActiveRevenue: number;
  }> {
    const params: ActiveSessionsParams = { actorIds };
    const response = await this.api.getActiveSessions(params);
    const items = Array.isArray(response.items) ? response.items : [];

    const sessions: ActiveSession[] = items.map(s => ({
      sessionId: s.sessionId.toString(),
      machineId: s.machineId,
      machineName: s.machineName,
      machineNumber: s.machineNumber,
      locationName: s.actorName || '',
      connectorId: s.connectorId,
      connectorType: 'Type2', // Not in API response, default
      startTime: new Date(s.startTime),
      durationMinutes: s.durationMinutes,
      energyKwh: s.currentEnergyKwh,
      currentPowerKw: s.power || 0,
      estimatedCostIls: s.estimatedCost || 0,
      stateOfCharge: undefined, // Not in API response
      customerName: s.accountName,
      idToken: s.idToken,
    }));

    return {
      sessions,
      totalActiveEnergy: response.totalActiveEnergyKwh ?? 0,
      totalActiveRevenue: response.totalEstimatedRevenue ?? 0,
    };
  }

  async getRecentSessions(
    page: number = 0,
    pageSize: number = 50,
    hours: number = 24
  ): Promise<{ sessions: RecentSession[]; totalCount: number }> {
    const params: RecentSessionsParams = {
      hours,
      skip: page * pageSize,
      take: pageSize,
    };

    const response = await this.api.getRecentSessions(params);
    const items = Array.isArray(response.items) ? response.items : [];

    const sessions: RecentSession[] = items.map(s => ({
      sessionId: s.sessionId,
      machineId: s.machineId,
      machineName: s.machineName,
      locationName: s.actorName || '',
      connectorType: 'Type2', // Not in API response, default
      startTime: new Date(s.startTime),
      stopTime: new Date(s.endTime),
      durationMinutes: s.durationMinutes,
      energyKwh: s.energyKwh,
      costIls: s.revenue, // Using revenue as cost display
      status: s.status as SessionStatus,
      paymentMethod: '', // Not in API response
      customerName: s.accountName,
    }));

    return {
      sessions,
      totalCount: response.totalCount ?? 0,
    };
  }

  // ============================================
  // Alerts Methods
  // ============================================

  async getAlertsSummary(): Promise<AlertSummary> {
    const response = await this.api.getAlertsSummary();

    return {
      totalActive: response.totalAlerts ?? 0,
      criticalCount: response.connectivityIssues ?? 0, // Connectivity issues are often critical
      warningCount: response.failedPayments ?? 0,
      infoCount: response.failedCalculations ?? 0,
      connectivityIssues: response.connectivityIssues ?? 0,
      failedCalculations: response.failedCalculations ?? 0,
      failedPayments: response.failedPayments ?? 0,
    };
  }

  async getAlerts(filters?: AlertFilters): Promise<{ alerts: Alert[]; totalCount: number }> {
    const params: AlertsParams = {
      type: filters?.type?.[0] as AlertsParams['type'],
      pendingOnly: filters?.status?.includes('Active'),
      skip: 0,
      take: 100,
    };

    const response = await this.api.getAlerts(params);
    const items = Array.isArray(response.items) ? response.items : [];

    const alerts: Alert[] = items.map(a => ({
      alertId: String(a.id ?? ''),
      type: this.mapAlertType(a.type),
      machineId: a.machineId,
      machineName: a.machineName,
      locationName: a.actorName,
      severity: this.mapAlertSeverity(a.severity),
      status: a.isHandled ? 'Resolved' : 'Active',
      title: a.typeName || this.getAlertTitle(a.type),
      description: a.details,
      createdAt: a.timestamp ? new Date(a.timestamp) : new Date(),
      acknowledgedAt: undefined,
      resolvedAt: undefined,
    }));

    return {
      alerts,
      totalCount: response.totalCount ?? 0,
    };
  }

  private mapAlertType(type: string): AlertType {
    switch (type) {
      case 'connectivity':
        return 'Connectivity';
      case 'calculation':
        return 'Calculation';
      case 'payment':
        return 'Payment';
      default:
        return 'Connectivity';
    }
  }

  private getAlertTitle(type: string): string {
    switch (type) {
      case 'connectivity':
        return 'Connectivity Issue';
      case 'calculation':
        return 'Calculation Error';
      case 'payment':
        return 'Payment Failed';
      default:
        return 'Alert';
    }
  }

  private mapAlertSeverity(severity: string): AlertSeverity {
    switch (severity) {
      case 'high':
        return 'Critical';
      case 'medium':
        return 'Warning';
      case 'low':
        return 'Info';
      default:
        return 'Warning';
    }
  }

  // ============================================
  // Accounts Methods
  // ============================================

  async getAccountSummary(): Promise<AccountSummary> {
    const response = await this.api.getAccountsSummary();

    return {
      totalAccounts: response.totalAccounts,
      activeAccounts: response.activeAccounts,
      blockedAccounts: response.blockedAccounts,
      pendingAccounts: response.pendingAccounts,
      deactivatedAccounts: response.deactivatedAccounts,
      activeSubscriptions: response.activeSubscriptions,
      expiringSubscriptions: response.expiringSubscriptions,
      expiringPaymentCards: response.expiringPaymentCards,
      expiredPaymentCards: response.expiredPaymentCards,
      paymentsToday: response.paymentsToday,
      successfulPaymentsCountToday: response.successfulPaymentsCountToday,
      failedPaymentsToday: response.failedPaymentsToday,
    };
  }

  async getSubscriptions(page: number = 0, pageSize: number = 10, expiringOnly?: boolean): Promise<{ subscriptions: Subscription[]; totalCount: number }> {
    const params: SubscriptionsParams = {
      expiringOnly,
      skip: page * pageSize,
      take: pageSize,
    };

    const response = await this.api.getSubscriptions(params);
    const items = Array.isArray(response.items) ? response.items : [];

    const subscriptions: Subscription[] = items.map(s => ({
      subscriptionId: String(s.relationId ?? s.subscriptionId ?? ''),
      accountId: s.accountId,
      accountName: s.accountName,
      machineId: s.machineId,
      machineName: s.machineName,
      planName: s.planName ?? 'Standard', // Default if not in API response
      type: (s.type as 'Owner' | 'Guest') ?? 'Owner',
      monthlyFeeIls: s.monthlyFee ?? 0, // Default if not in API response
      startDate: new Date(s.startDate),
      expirationDate: s.expirationDate ? new Date(s.expirationDate) : null,
      isExpiringSoon: s.isExpiring ?? false,
      status: (s.status as 'Active' | 'Suspended' | 'Cancelled') ?? 'Active',
    }));

    return {
      subscriptions,
      totalCount: response.totalCount ?? 0,
    };
  }

  async getPaymentsToday(): Promise<{ payments: PaymentToday[]; totalAmount: number; failedCount: number }> {
    const response = await this.api.getPaymentsToday();
    const items = Array.isArray(response.payments) ? response.payments : [];

    const payments: PaymentToday[] = items.map(p => ({
      paymentId: String(p.paymentId ?? ''),
      sessionId: String(p.sessionId ?? ''),
      accountName: p.accountName,
      amount: p.amount,
      currency: p.currency ?? p.currencyCode,
      paymentMethod: p.paymentMethod,
      timestamp: new Date(p.timestamp),
      status: p.status as 'Completed' | 'Pending' | 'Failed',
    }));

    return {
      payments,
      totalAmount: response.totalAmount ?? 0,
      failedCount: response.failedCount ?? 0,
    };
  }
}
